import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import api from "../lib/api";
import CompanyCard from "../components/CompanyCard";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Search, MapPin, Loader2, ArrowLeft, List, X, ChevronDown, Layers, Map as MapIcon, ListOrdered, Home, SlidersHorizontal } from "lucide-react";
import { OuroBadge, CashbackBadge, PlanPill } from "../components/Badges";
import RouteButton from "../components/RouteButton";
import { useAuth } from "../context/AuthContext";

const TILE_STYLES = {
  color: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    label: "Colorido",
  },
  light: {
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution: '&copy; OpenStreetMap &copy; CARTO',
    label: "Cinza/Claro",
  },
  dark: {
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: '&copy; OpenStreetMap &copy; CARTO',
    label: "Escuro",
  },
};

function pinIcon(plan, logoUrl) {
  const isGratis = !plan || plan === "GRATIS";
  const klass = plan === "PLATINUM" ? "platinum" : plan === "OURO" ? "ouro" : plan === "PRATA" ? "prata" : plan === "BRONZE" ? "bronze" : "gratis";
  // Free plan: tiny dot, no logo
  const inner = isGratis
    ? ""
    : (logoUrl
        ? `<img src="${logoUrl}" class="mb-pin-logo" alt=""/>`
        : `<span>${(plan || "G")[0]}</span>`);
  const html = `<div class="mb-pin ${klass}">${inner}</div>`;
  // Hierarchy: PLATINUM 64 > OURO 56 > PRATA 48 > BRONZE 42 > GRATIS 18 (tiny)
  const size = isGratis
    ? [18, 18]
    : plan === "PLATINUM"
      ? [64, 64]
      : plan === "OURO"
        ? [56, 56]
        : plan === "PRATA"
          ? [48, 48]
          : [42, 42]; // BRONZE
  return L.divIcon({
    className: "mb-marker",
    html,
    iconSize: size,
    iconAnchor: [size[0] / 2, size[1] + 4],
    popupAnchor: [0, -size[1]],
  });
}

function FlyTo({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, 13, { animate: true }); }, [center, map]);
  return null;
}

function FiltersPanel({ search, setSearch, category, setCategory, onlyCashback, setOnlyCashback, categories, onApply }) {
  return (
    <div className="space-y-2.5">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
        <Input
          placeholder="Buscar empresa..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 rounded-xl border-stone-200 bg-white"
          data-testid="discover-search"
        />
      </div>
      <Select value={category} onValueChange={(v) => { setCategory(v); if (onApply) onApply(); }}>
        <SelectTrigger className="rounded-xl border-stone-200 text-sm bg-white" data-testid="discover-category">
          <SelectValue placeholder="Categoria"/>
        </SelectTrigger>
        <SelectContent>
          {categories.map((c) => (
            <SelectItem key={c} value={c}>{c === "ALL" ? "Todas categorias" : c}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer select-none px-1">
        <Checkbox checked={onlyCashback} onCheckedChange={(v) => setOnlyCashback(!!v)} data-testid="discover-cashback-filter"/>
        Apenas com cashback
      </label>
    </div>
  );
}

export default function DiscoverMap() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const isAll = citySlug === "all";
  const [allCities, setAllCities] = useState([]);
  const [city, setCity] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState(() => searchParams.get("category") || "ALL");
  const [onlyCashback, setOnlyCashback] = useState(false);
  const [activeCompany, setActiveCompany] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [mapStyle, setMapStyle] = useState(() => localStorage.getItem("mb_map_style") || "color");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("mb_view_mode") || "map"); // "map" | "list"
  const [userPos, setUserPos] = useState(null); // {lat, lng}
  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);  // slug
  const [segmentsEnabled, setSegmentsEnabled] = useState(true);
  const { user } = useAuth();
  const markerRefs = useRef({});

  // Reagir a mudança do query ?category=... (ex.: clique no CategoryRail)
  useEffect(() => {
    const c = searchParams.get("category");
    if (c && c !== category) setCategory(c);
  }, [searchParams, category]);

  useEffect(() => { api.get("/cities").then((r) => setAllCities(r.data)); }, []);
  // Solicita geolocalização ao montar para calcular distâncias
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => { /* user negou — sem distância */ },
      { timeout: 10000, maximumAge: 5 * 60 * 1000 }
    );
  }, []);
  useEffect(() => {
    api.get("/segments?active=true").then((r) => setSegments(r.data || [])).catch(() => {});
    api.get("/settings/public").then((r) => setSegmentsEnabled(r.data?.discover_by_segments_enabled !== false)).catch(() => {});
    const seg = searchParams.get("segment");
    if (seg) setSelectedSegment(seg);
  }, [searchParams]);

  const changeMapStyle = (s) => {
    setMapStyle(s);
    localStorage.setItem("mb_map_style", s);
  };
  const changeViewMode = (m) => {
    setViewMode(m);
    localStorage.setItem("mb_view_mode", m);
  };

  useEffect(() => {
    if (user?.role === "CUSTOMER") {
      api.get("/favorites").then((r) => setFavorites(r.data)).catch(() => {});
    } else { setFavorites([]); }
  }, [user]);

  const toggleFavorite = (id, fav) => {
    setFavorites((prev) => fav ? [...prev, id] : prev.filter((x) => x !== id));
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const cityFetch = isAll ? Promise.resolve({ data: null }) : api.get(`/cities/${citySlug}`);
    const params = { search: search || undefined, category: category === "ALL" ? undefined : category, acceptsCashback: onlyCashback ? true : undefined };
    if (!isAll) params.citySlug = citySlug;
    if (user?.id) params.user_id = user.id;
    if (segmentsEnabled && selectedSegment) params.segment = selectedSegment;
    Promise.all([cityFetch, api.get(`/companies`, { params })])
      .then(([c1, c2]) => {
        if (!alive) return;
        setCity(c1.data);
        setCompanies(c2.data);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [citySlug, search, category, onlyCashback, isAll, user?.id, selectedSegment, segmentsEnabled]);

  const PLAN_RANK = { PLATINUM: 0, OURO: 1, PRATA: 2, BRONZE: 3, GRATIS: 4 };
  const haversine = (a, b) => {
    if (!a || !b) return null;
    const toRad = (d) => d * Math.PI / 180;
    const R = 6371; // km
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat), lat2 = toRad(b.lat);
    const h = Math.sin(dLat/2)**2 + Math.sin(dLng/2)**2 * Math.cos(lat1) * Math.cos(lat2);
    return 2 * R * Math.asin(Math.sqrt(h));
  };
  const sortedCompanies = useMemo(() => {
    const list = companies.map((c) => ({
      ...c,
      distance_km: (userPos && c.lat && c.lng) ? haversine(userPos, { lat: c.lat, lng: c.lng }) : null,
    }));
    return list.sort((a, b) => {
      const ra = PLAN_RANK[a.plan] ?? 5;
      const rb = PLAN_RANK[b.plan] ?? 5;
      if (ra !== rb) return ra - rb;
      // Dentro do mesmo plano, ordena por distância se temos coordenadas
      if (a.distance_km != null && b.distance_km != null) return a.distance_km - b.distance_km;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [companies, userPos]);

  const categories = useMemo(() => {
    const set = new Set(companies.map((c) => c.category));
    return ["ALL", ...Array.from(set).sort()];
  }, [companies]);

  const center = useMemo(() => {
    if (isAll && allCities.length > 0) {
      const lat = allCities.reduce((a, c) => a + c.lat, 0) / allCities.length;
      const lng = allCities.reduce((a, c) => a + c.lng, 0) / allCities.length;
      return [lat, lng];
    }
    return city ? [city.lat, city.lng] : [-10.9472, -37.0731];
  }, [city, allCities, isAll]);

  const focusCompany = (c) => {
    setActiveCompany(c);
    setListOpen(false);
  };

  const CitySwitcher = ({ className = "" }) => (
    <Select value={isAll ? "__all__" : citySlug} onValueChange={(v) => navigate(`/discover/${v === "__all__" ? "all" : v}`)}>
      <SelectTrigger className={`rounded-xl bg-white border-stone-200 text-stone-900 font-semibold ${className}`} data-testid="city-switcher">
        <SelectValue placeholder="Escolher cidade"/>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">📍 Todas as cidades</SelectItem>
        {allCities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}/{c.state}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  // City hero
  const CityHero = ({ compact = false, onBack }) => (
    <div className={`relative overflow-hidden ${compact ? "h-32" : "h-44"}`}>
      {city?.cover_image && !isAll ? (
        <img src={city.cover_image} alt={city.name} className="absolute inset-0 w-full h-full object-cover"/>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 to-blue-950"/>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-blue-950/95 via-blue-950/60 to-blue-950/30"/>
      <div className="relative h-full p-4 flex flex-col justify-end text-white">
        {onBack ? (
          <button onClick={onBack} className="absolute top-3 left-3 inline-flex items-center gap-1 text-[11px] text-white/85 hover:text-white bg-white/10 backdrop-blur px-2 py-1 rounded-md" data-testid="back-link">
            <ArrowLeft size={11}/> Voltar
          </button>
        ) : (
          <Link to="/" className="absolute top-3 left-3 inline-flex items-center gap-1 text-[11px] text-white/85 hover:text-white bg-white/10 backdrop-blur px-2 py-1 rounded-md" data-testid="back-link">
            <ArrowLeft size={11}/> Voltar
          </Link>
        )}
        <h1 className="font-display text-2xl font-bold leading-tight mb-1" data-testid="discover-city-title">
          {isAll ? "Todas as cidades" : (city?.name || "Carregando...")}
        </h1>
        <CitySwitcher className="w-full h-9 text-sm"/>
      </div>
    </div>
  );

  return (
    <div className="w-full relative h-[calc(100dvh-4rem)]" data-testid="discover-shell">
      {/* DESKTOP layout: sidebar + map split */}
      <div className="hidden lg:grid h-full grid-cols-12">
        <aside className="col-span-4 xl:col-span-3 bg-stone-50 border-r border-stone-200 flex flex-col h-full overflow-hidden">
          <CityHero />
          <div className="p-4 border-b border-stone-200 bg-white">
            <FiltersPanel {...{ search, setSearch, category, setCategory, onlyCashback, setOnlyCashback, categories }} />
          </div>
          {segmentsEnabled && segments.length > 0 && (
            <div className="p-3 border-b border-stone-200 bg-stone-50 space-y-2" data-testid="segments-grid">
              <p className="text-[10px] font-bold uppercase tracking-wider text-stone-500 px-1">Filtrar por segmento</p>
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
                {segments.map((s) => {
                  const isSelected = selectedSegment === s.slug;
                  return (
                    <button
                      key={s.slug}
                      onClick={() => setSelectedSegment(isSelected ? null : s.slug)}
                      className={`shrink-0 rounded-xl overflow-hidden border transition-all w-[140px] ${isSelected ? "border-amber-400 ring-2 ring-amber-300 shadow-md" : "border-stone-200 hover:border-amber-400"}`}
                      data-testid={`seg-pick-${s.slug}`}
                    >
                      <div className="aspect-[16/9] bg-gradient-to-br from-amber-100 to-blue-100 overflow-hidden relative">
                        {s.cover_url ? <img src={s.cover_url} alt={s.name} loading="lazy" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-2xl">{s.name[0]}</div>}
                        <div className="absolute inset-0 bg-gradient-to-t from-blue-950/70 to-transparent flex items-end p-1.5">
                          <span className="text-white font-display font-bold text-[11px] leading-tight line-clamp-2">{s.cta || s.name}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="company-list">
            {loading && (
              <div className="flex items-center justify-center py-12 text-stone-400 text-sm">
                <Loader2 className="animate-spin mr-2" size={16}/> Carregando...
              </div>
            )}
            {!loading && companies.length === 0 && (
              <p className="text-sm text-stone-500 text-center py-12">Nenhuma empresa encontrada{selectedSegment ? " nesse segmento" : ""}.</p>
            )}
            {companies.map((c) => (
              <CompanyCard key={c.id} company={c} active={activeCompany?.id === c.id} onClick={() => focusCompany(c)} favorites={favorites} onToggleFavorite={toggleFavorite}/>
            ))}
          </div>
        </aside>

        <div className="col-span-8 xl:col-span-9 relative z-0">
          <MapView center={center} companies={companies} markerRefs={markerRefs} setActive={setActiveCompany} />
        </div>
      </div>

      {/* MOBILE/TABLET layout: full-screen map OR list view */}
      <div className="lg:hidden absolute inset-0 z-0">
        {viewMode === "map" ? (
          <>
            <MapView center={center} companies={companies} markerRefs={markerRefs} setActive={setActiveCompany} mapStyle={mapStyle} mobile />
            <div className="absolute top-16 right-3 z-[400] lg:hidden">
              <MapStyleToggle mapStyle={mapStyle} onChange={changeMapStyle}/>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 bg-stone-50 overflow-y-auto pt-[68px] pb-20" data-testid="mobile-list-view">
            {segmentsEnabled && selectedSegment && (
              <div className="mx-3 mb-2 px-3 py-2 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-between" data-testid="active-segment-pill">
                <span className="text-xs text-blue-900 font-semibold">Segmento: {selectedSegment}</span>
                <button onClick={() => setSelectedSegment(null)} className="text-xs font-bold text-blue-900" data-testid="seg-clear-listview" aria-label="Limpar segmento">
                  <X size={14}/>
                </button>
              </div>
            )}
            <div className="px-3 space-y-2">
              {loading && <div className="text-center text-stone-400 py-12 text-sm"><Loader2 className="animate-spin inline mr-2" size={16}/>Carregando...</div>}
              {!loading && sortedCompanies.length === 0 && <p className="text-sm text-stone-500 text-center py-12">Nenhuma empresa encontrada. Toque no filtro para ajustar.</p>}
              {sortedCompanies.map((c) => (
                <CompanyCard key={c.id} company={c} onClick={() => navigate(`/company/${c.id}`)} favorites={favorites} onToggleFavorite={toggleFavorite}/>
              ))}
            </div>
          </div>
        )}

        {/* Top floating bar: home + city pill + view-mode toggle + filters drawer */}
        <div className="absolute top-3 left-3 right-3 z-[400] flex items-center gap-2 pointer-events-none">
          <Link to="/" className="pointer-events-auto inline-flex items-center gap-1 bg-white/95 backdrop-blur shadow-md px-3 py-2 rounded-full text-xs font-semibold text-blue-950" data-testid="back-link-mobile" title="Início">
            <Home size={14}/>
          </Link>
          <div className="pointer-events-auto flex-1 bg-white/95 backdrop-blur shadow-md rounded-full overflow-hidden">
            <CitySwitcher className="border-0 bg-transparent h-9 text-xs font-semibold"/>
          </div>
          {/* Map/List toggle */}
          <div className="pointer-events-auto inline-flex items-center bg-white/95 backdrop-blur shadow-md rounded-full p-0.5" data-testid="view-toggle">
            <button onClick={() => changeViewMode("map")} className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-bold transition-colors ${viewMode === "map" ? "bg-blue-900 text-white" : "text-stone-600"}`} data-testid="view-map">
              <MapIcon size={12}/>
            </button>
            <button onClick={() => changeViewMode("list")} className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-bold transition-colors ${viewMode === "list" ? "bg-blue-900 text-white" : "text-stone-600"}`} data-testid="view-list">
              <ListOrdered size={12}/>
            </button>
          </div>
          {/* Filtros (sheet) — disponível em ambos os modos */}
          <Sheet open={listOpen} onOpenChange={setListOpen}>
            <SheetTrigger asChild>
              <button className="pointer-events-auto inline-flex items-center gap-1.5 bg-blue-950 text-white px-3 py-2 rounded-full text-xs font-semibold shadow-md" data-testid="mobile-filters-btn" title="Filtros">
                <SlidersHorizontal size={14}/>
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[90%] sm:w-[420px] p-0 overflow-hidden flex flex-col">
              <CityHero compact onBack={() => setListOpen(false)} />
              <div className="p-4 border-b border-stone-200">
                <FiltersPanel {...{ search, setSearch, category, setCategory, onlyCashback, setOnlyCashback, categories }} onApply={() => setListOpen(false)} />
              </div>
              {segmentsEnabled && segments.length > 0 && (
                <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="segments-grid-mobile">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-stone-500 px-1">Escolha o que procurar</p>
                  {segments.map((s) => {
                    const isSelected = selectedSegment === s.slug;
                    return (
                      <button
                        key={s.slug}
                        onClick={() => {
                          // toggle: clicar no já selecionado limpa o filtro
                          setSelectedSegment(isSelected ? null : s.slug);
                          setListOpen(false);
                        }}
                        className={`w-full text-left rounded-2xl overflow-hidden bg-white border transition-all ${isSelected ? "border-amber-400 ring-2 ring-amber-300 shadow-md" : "border-stone-200 hover:border-amber-400"}`}
                        data-testid={`seg-pick-mobile-${s.slug}`}
                      >
                        <div className="aspect-[16/8] bg-gradient-to-br from-amber-100 to-blue-100 overflow-hidden relative">
                          {s.cover_url ? <img src={s.cover_url} alt={s.name} loading="lazy" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-2xl">{s.name[0]}</div>}
                          <div className="absolute inset-0 bg-gradient-to-t from-blue-950/70 to-transparent flex items-end p-2">
                            <span className="text-white font-display font-bold text-sm">{s.cta || s.name}</span>
                          </div>
                          {isSelected && (
                            <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-400 text-amber-950 shadow">Selecionado</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>

        {/* Bottom mobile filter quick row (apenas no modo mapa) — acima do BottomTabBar */}
        {viewMode === "map" && (
          <div className="absolute bottom-20 left-3 right-3 z-[400] pointer-events-none">
            <div className="pointer-events-auto bg-white/95 backdrop-blur shadow-lg rounded-2xl p-2 flex gap-2 overflow-x-auto">
              {categories.slice(0, 6).map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${category === c ? "bg-blue-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
                  data-testid={`mobile-cat-${c}`}
                >
                  {c === "ALL" ? "Todas" : c}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Active company bottom-sheet (mobile + desktop popup-like) */}
      {activeCompany && (
        <div className="lg:hidden absolute bottom-[148px] left-3 right-3 z-[450] pointer-events-none">
          <CompanyQuickCard company={activeCompany} onClose={() => setActiveCompany(null)} onOpen={() => navigate(`/company/${activeCompany.id}`)} />
        </div>
      )}
      {activeCompany && (
        <div className="hidden lg:block absolute bottom-6 right-6 w-80 z-[450]">
          <CompanyQuickCard company={activeCompany} onClose={() => setActiveCompany(null)} onOpen={() => navigate(`/company/${activeCompany.id}`)} />
        </div>
      )}
    </div>
  );
}

function InvalidateOnResize() {
  const map = useMap();
  React.useEffect(() => {
    const fix = () => { try { map.invalidateSize(); } catch (_) { /* ignore */ } };
    // Roda algumas vezes para pegar a transição da URL bar mobile
    const timers = [setTimeout(fix, 100), setTimeout(fix, 400), setTimeout(fix, 1200)];
    window.addEventListener("resize", fix);
    window.addEventListener("orientationchange", fix);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", fix);
      window.removeEventListener("orientationchange", fix);
    };
  }, [map]);
  return null;
}

function MapView({ center, companies, markerRefs, setActive, mobile, mapStyle = "color" }) {
  const tile = TILE_STYLES[mapStyle] || TILE_STYLES.color;
  return (
    <MapContainer
      center={center}
      zoom={mobile ? 12 : 13}
      scrollWheelZoom
      zoomControl={!mobile}
      attributionControl={false}
      className="h-full w-full"
      data-testid="discover-map"
    >
      <TileLayer key={mapStyle} attribution="" url={tile.url} />
      <FlyTo center={center} />
      <InvalidateOnResize />
      {companies.map((c) => (
        <Marker
          key={c.id}
          position={[c.lat, c.lng]}
          icon={pinIcon(c.plan, c.logo_url)}
          ref={(ref) => { if (ref) markerRefs.current[c.id] = ref; }}
          eventHandlers={{ click: () => setActive(c) }}
        />
      ))}
      {/* Marcadores das filiais (mesma empresa, ponto adicional) */}
      {companies.flatMap((c) => (c.branches || [])
        .filter((b) => b && b.active !== false && b.lat && b.lng)
        .map((b, bi) => (
          <Marker
            key={`${c.id}__branch__${b.id || bi}`}
            position={[parseFloat(b.lat), parseFloat(b.lng)]}
            icon={pinIcon(c.plan, c.logo_url)}
            eventHandlers={{ click: () => setActive({ ...c, _activeBranchName: b.name || `Filial ${bi + 1}`, address: b.address || c.address }) }}
          />
        ))
      )}
    </MapContainer>
  );
}

function MapStyleToggle({ mapStyle, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="absolute top-3 right-3 z-[400]" data-testid="map-style-toggle">
      <Button size="sm" variant="outline" onClick={() => setOpen(!open)} aria-label="Estilo do mapa"
        title={`Estilo do mapa: ${TILE_STYLES[mapStyle]?.label}`}
        className="bg-white shadow-md rounded-xl border-stone-300 hover:bg-stone-50" data-testid="map-style-btn">
        <Layers size={14} className="mr-1.5"/> <span>Estilo: {TILE_STYLES[mapStyle]?.label || "Mapa"}</span>
      </Button>
      {open && (
        <div className="absolute right-0 mt-1 w-44 bg-white rounded-xl shadow-2xl border border-stone-200 overflow-hidden">
          {Object.entries(TILE_STYLES).map(([key, val]) => (
            <button key={key} onClick={() => { onChange(key); setOpen(false); }}
              className={`block w-full text-left px-3 py-2 text-sm hover:bg-stone-50 ${mapStyle === key ? "bg-blue-50 text-blue-950 font-semibold" : "text-stone-700"}`}
              data-testid={`map-style-${key}`}>
              {val.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CompanyQuickCard({ company, onClose, onOpen }) {
  const isOuro = company.plan === "OURO";
  const showCashback = company.accepts_cashback && (company.plan === "PRATA" || company.plan === "OURO");
  return (
    <div className={`pointer-events-auto rounded-2xl bg-white shadow-2xl border ${isOuro ? "border-amber-400 ouro-glow" : "border-stone-200"} overflow-hidden`} data-testid="company-quick-card">
      {company.cover_url && (
        <div className="aspect-[16/8] bg-stone-100 overflow-hidden relative">
          <img src={company.cover_url} alt={company.name} className="w-full h-full object-cover"/>
          <button onClick={onClose} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/40 backdrop-blur text-white hover:bg-black/60 flex items-center justify-center" aria-label="Fechar"><X size={14}/></button>
        </div>
      )}
      <div className="p-4">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {company.logo_url && <img src={company.logo_url} alt="" className="w-8 h-8 rounded-lg object-contain bg-white border border-stone-200 shrink-0 p-0.5"/>}
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-semibold text-stone-900 text-base leading-tight truncate">{company.name}</h3>
            {company._activeBranchName && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-blue-900 text-[10px] font-bold mt-0.5" data-testid="quick-branch-tag">
                Filial: {company._activeBranchName}
              </span>
            )}
          </div>
          <PlanPill plan={company.plan}/>
        </div>
        {!company.cover_url && <button onClick={onClose} className="text-stone-400 hover:text-stone-700 -mt-1 shrink-0" aria-label="Fechar"><X size={16}/></button>}
      </div>
      <p className="text-xs text-stone-500 line-clamp-2 mb-2">{company.description || company.category}</p>
      <div className="flex items-center gap-1.5 text-[11px] text-stone-500 mb-3">
        <MapPin size={11}/> {company.address || company.category}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {isOuro && <OuroBadge/>}
        {showCashback && <CashbackBadge/>}
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-stone-100 text-stone-600">{company.category}</span>
      </div>
      <div className="flex gap-2">
        <Button onClick={onOpen} size="sm" className="flex-1 bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="open-company-detail">
          Ver detalhes
        </Button>
        <RouteButton lat={company.lat} lng={company.lng} label={company.name} size="sm" className="shrink-0"/>
      </div>
      </div>
    </div>
  );
}
